<script>
import { GlFormInput, GlFormGroup } from '@gitlab/ui';
import * as Sentry from '@sentry/browser';
import { throttle } from 'lodash-es';
import TransactionCard from '~/javascripts/purchase/components/transaction_card.vue';
import { sprintf } from '~/javascripts/lib/locale';
import { purchaseDuoProPayload } from '~/javascripts/subscription_payloads';
import { purchaseChangeSubscription } from '~/javascripts/api';
import AddSeatsInvoice from '~/javascripts/subscription/components/add_seats_invoice.vue';
import {
  ADD_DUO_PRO_SEATS_TRANSACTION,
  BUY_DUO_PRO_SEATS_TRANSACTION,
  ERROR_TOO_MANY_ADDON_SEATS_MESSAGE,
} from '~/javascripts/purchase/constants';
import PurchaseConfirmationCard from '~/javascripts/purchase/components/purchase_confirmation_card_deprecated.vue';
import PaymentMethodsSelect from '~/javascripts/purchase/shared/components/payment_methods_select.vue';
import {
  DEFAULT_DEBOUNCE_AND_THROTTLE_MS,
  ERROR_CODE_REQUEST_CANCELED,
} from '~/javascripts/constants';
import { GITLAB_COM_PLAN_TYPE } from '~/javascripts/subscription/constants';
import { ERROR_TOO_MANY_ADDON_SEATS_TYPE } from '~/javascripts/purchase/error_dictionary';
import Tracking from '~/javascripts/tracking';
import {
  TRACKING_ACTIONS,
  TRACKING_LABELS,
  TRACKING_CATEGORIES,
} from '~/javascripts/tracking/constants';

export const emptyInvoice = {
  amount: 0,
  amount_without_tax: 0,
  tax_amount: 0,
};

const i18n = Object.freeze({
  inputDescription:
    'You have %{seatQuantity} seats of %{subscriptionName} in your subscription. The maximum amount of seats of GitLab Duo Pro available for you to buy is %{availableSeats}.',
  additionalSeats: 'Add additional seats',
  seats: 'Seats',
  nullValidation: 'Please select at least 1 seat.',
  tooManySeatsValidation:
    'The amount of seats purchased for GitLab Duo Pro cannot be more than the amount of seats in your %{subscriptionName} subscription.',
  selectMoreSeats: 'Select more seats',
  selectSeats: 'Select seats',
});

export default {
  name: 'DuoProSeatsForm',
  components: {
    TransactionCard,
    GlFormInput,
    GlFormGroup,
    AddSeatsInvoice,
    PurchaseConfirmationCard,
    PaymentMethodsSelect,
  },
  inject: ['subscription', 'planId'],
  props: {
    currentSeats: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      selectedSeats: 1,
      selectedPaymentMethodId: null,
      abortController: null,
      isLoading: false,
      invoice: undefined,
      invoiceItems: [],
      didMakePayment: false,
    };
  },
  computed: {
    didSelectEnoughSeats() {
      return this.selectedSeats >= 1;
    },
    hasSelectedTooManySeats() {
      return this.totalSeats > this.subscription.seatQuantity;
    },
    addSeatsErrorMessage() {
      if (this.hasSelectedTooManySeats) {
        return sprintf(i18n.tooManySeatsValidation, {
          subscriptionName: this.subscription.mainPlan?.name,
        });
      }

      return !this.didSelectEnoughSeats ? this.addSeatsMessage : '';
    },
    isValidNumberOfSeats() {
      return this.didSelectEnoughSeats && !this.hasSelectedTooManySeats;
    },
    inputDescription() {
      const availableSeatsForPurchase = this.subscription.seatQuantity - this.currentSeats;

      return sprintf(i18n.inputDescription, {
        seatQuantity: this.subscription.seatQuantity,
        subscriptionName: this.subscription.mainPlan?.name,
        availableSeats: availableSeatsForPurchase,
      });
    },
    addSeatsMessage() {
      return i18n.nullValidation;
    },
    totalSeats() {
      return this.didSelectEnoughSeats ? this.selectedSeats + this.currentSeats : '–';
    },
    total() {
      return this.invoice?.amount ?? 0;
    },
    totalValue() {
      return this.didSelectEnoughSeats ? this.total : 0;
    },
    hasCurrentSeats() {
      return this.currentSeats >= 1;
    },
    getTitle() {
      return this.hasCurrentSeats ? i18n.selectMoreSeats : i18n.selectSeats;
    },
    borderClasses() {
      return this.hasCurrentSeats
        ? 'gl-border-gray-100 gl-border-b-solid gl-border-b-1 gl-pb-5 gl-mb-5'
        : '';
    },
    inputLabel() {
      return this.hasCurrentSeats ? i18n.additionalSeats : i18n.seats;
    },
    invoiceValue() {
      return this.isValidNumberOfSeats ? this.invoice : { ...this.invoice, ...emptyInvoice };
    },
    canProceedWithPayment() {
      return Boolean(this.selectedPaymentMethodId);
    },
    hasSelectedSeats() {
      return this.selectedSeats > 0;
    },
    canProceedWithPurchase() {
      return (
        this.hasSelectedSeats &&
        this.didSelectEnoughSeats &&
        this.canProceedWithPayment &&
        !this.hasSelectedTooManySeats &&
        !this.didMakePayment &&
        !this.isLoading
      );
    },
    isSaas() {
      return this.subscription.type === GITLAB_COM_PLAN_TYPE;
    },
  },
  created() {
    this.updateInvoiceSummary();
  },
  methods: {
    handlePaymentMethodSelect(paymentMethodId) {
      this.selectedPaymentMethodId = paymentMethodId;
    },
    handleSeatsChanges() {
      if (this.hasSelectedTooManySeats) {
        this.$emit(
          'error',
          new Error(ERROR_TOO_MANY_ADDON_SEATS_MESSAGE, { cause: ERROR_TOO_MANY_ADDON_SEATS_TYPE }),
        );
        return;
      }

      if (this.didSelectEnoughSeats) {
        this.updateInvoiceSummary();
      }
    },
    getAddSeatsPayload({ isPurchase }) {
      return purchaseDuoProPayload({
        subscriptionName: this.subscription.name,
        paymentMethodId: isPurchase ? this.selectedPaymentMethodId : null,
        isConfirmed: isPurchase,
        orderTotal: this.invoice?.amount ?? 0,
        totalWithoutTaxes: this.invoice?.amount_without_tax ?? 0,
        transaction: ADD_DUO_PRO_SEATS_TRANSACTION,
        products: {
          add_on: {
            plan_id: this.planId,
            quantity: this.selectedSeats,
          },
        },
      });
    },
    getBuySeatsPayload({ isPurchase }) {
      return purchaseDuoProPayload({
        subscriptionName: this.subscription.name,
        paymentMethodId: isPurchase ? this.selectedPaymentMethodId : null,
        isConfirmed: isPurchase,
        orderTotal: this.invoice?.amount ?? 0,
        totalWithoutTaxes: this.invoice?.amount_without_tax ?? 0,
        transaction: BUY_DUO_PRO_SEATS_TRANSACTION,
        products: {
          add_on: {
            plan_id: this.planId,
            quantity: this.selectedSeats,
          },
        },
        newProductId: this.planId,
        newProductQuantity: this.selectedSeats,
      });
    },
    generatePayload({ isPurchase }) {
      return this.hasCurrentSeats
        ? this.getAddSeatsPayload({ isPurchase })
        : this.getBuySeatsPayload({ isPurchase });
    },
    previewSuccess({ invoice_data: response }) {
      this.invoice = response.invoice;
      this.invoiceItems = response.invoice_item;
    },
    updateInvoiceSummary: throttle(async function updateInvoiceSummary() {
      this.isLoading = true;
      this.clearError();

      const payload = this.generatePayload({ isPurchase: false });

      try {
        // Abort any previous request
        this.abortController?.abort();
        this.abortController = new AbortController();
        const response = await purchaseChangeSubscription(
          this.subscription.name,
          payload,
          this.abortController.signal,
        );
        this.isLoading = false;
        this.previewSuccess(response);
      } catch (error) {
        if (error.code === ERROR_CODE_REQUEST_CANCELED) {
          // This request was aborted, no need to display an error message
          // Keep loading as another one has taken its place
          return;
        }
        this.isLoading = false;
        this.handleError(error);
      }
    }, DEFAULT_DEBOUNCE_AND_THROTTLE_MS),
    handlePaymentSuccess(response) {
      if (response.success !== true) {
        throw new Error(response.errors);
      }

      if (window.gon?.global?.jh && response.pay_url) {
        window.location.href = response.pay_url;
        return;
      }

      this.didMakePayment = true;
      this.$emit('success');
    },
    handlePurchase() {
      this.isLoading = true;
      this.clearError();
      this.trackAddSeats();

      const payload = this.generatePayload({ isPurchase: true });

      return purchaseChangeSubscription(this.subscription.name, payload)
        .then(this.handlePaymentSuccess)
        .catch((error) => this.handleError(error))
        .finally(() => {
          this.isLoading = false;
        });
    },
    clearError() {
      this.$emit('error', null);
    },
    handleError(error) {
      this.$emit('error', error);
      this.reportError(error);
    },
    reportError(error) {
      Sentry.captureException(error, {
        tags: {
          vue_component: this.$options.name,
        },
      });
    },
    trackAddSeats() {
      const tracking = new Tracking();
      tracking.event(TRACKING_CATEGORIES.webstore, TRACKING_ACTIONS.clickPurchaseSeats, {
        label: this.isSaas ? TRACKING_LABELS.addDuoProSaaS : TRACKING_LABELS.addDuoProSM,
      });
    },
  },
};
</script>

<template>
  <div class="row gl-justify-between">
    <transaction-card :title="getTitle">
      <div
        v-if="hasCurrentSeats"
        class="gl-mb-5 gl-flex gl-justify-between gl-border-b-1 gl-border-gray-100 gl-pb-5 gl-border-b-solid"
      >
        <strong>Current seats</strong>
        <span>
          {{ currentSeats }}
        </span>
      </div>
      <div :class="borderClasses" data-testid="seat-input">
        <gl-form-group
          :label="inputLabel"
          label-for="seats"
          class="gl-mb-0"
          :description="inputDescription"
          :invalid-feedback="addSeatsErrorMessage"
        >
          <gl-form-input
            id="seats"
            v-model.number="selectedSeats"
            type="number"
            name="seats"
            :min="1"
            max="9999999999999"
            :state="isValidNumberOfSeats"
            @change="handleSeatsChanges"
          />
        </gl-form-group>
      </div>
      <div v-if="hasCurrentSeats" class="gl-flex gl-justify-between">
        <strong>New seats quantity</strong>
        <span data-testid="total-seats">{{ totalSeats }}</span>
      </div>
    </transaction-card>

    <add-seats-invoice
      :loading="isLoading"
      :invoice="invoiceValue"
      :invoice-items="invoiceItems"
      :show-info-message="didSelectEnoughSeats"
    />

    <purchase-confirmation-card
      :disabled="!canProceedWithPurchase"
      :loading="isLoading"
      :total="totalValue"
      card-title="Billing information"
      confirm-text="Purchase seats"
      @confirmPurchase="handlePurchase"
    >
      <template #payment-form>
        <payment-methods-select @click="handlePaymentMethodSelect" />
      </template>
    </purchase-confirmation-card>
  </div>
</template>
